import React, { memo, useCallback, useState, useEffect, useMemo } from 'react';
import IBAN from 'iban';
import { useI18n } from '../../../i18n';
import S from './style/Input';

const polandCountryCode = 'PL';

function Input({
  name,
  type = 'text',
  pattern,
  maskFn,
  label,
  required,
  disabled,
  readOnly,
  flat,
  error = null,
  dirty = false,
  value = '',
  requiredMessage = 'dlv_validation_required',
  patternMessage = 'dlv_validation_match',
  extraValidations = [],
  onChange,
  onBlur,
  onDirty,
  prefixLabel = '',
  verifyIban,
  formName,
  placeholder,
}: {
  name?: string;
  type?: string;
  pattern?: string | RegExp;
  maskFn?: (a: string) => string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  flat?: boolean;
  error?: any | null;
  dirty?: boolean;
  value?: string;
  requiredMessage?: string;
  patternMessage?: string;
  extraValidations?: any[];
  onChange?: (val: any, validation: any) => void;
  onBlur?: (val: any) => void;
  onDirty?: (val: any, validation: any) => void;
  prefixLabel?: string;
  verifyIban?: boolean;
  formName?: string;
  placeholder?: string;
}) {
  const i18n = useI18n();
  const [val, setVal] = useState(value);
  const [touched, setTouched] = useState(dirty);
  const [showValidation, setShowValidation] = useState(false);
  const [currentFormName, setFormName] = useState('');
  const [validity, setValidity] = useState<{
    variables?: any;
    valid: boolean;
    error: any;
  }>({
    valid: !error,
    error,
  });

  const validate = useCallback(
    currVal => {
      if (!touched) setTouched(true);
      if (required && currVal === '') {
        return { valid: false, error: requiredMessage };
      }
      if (pattern && typeof pattern === 'string' && !new RegExp(pattern).test(currVal)) {
        return { valid: false, error: patternMessage };
      }
      if (pattern && pattern instanceof RegExp && !pattern.test(currVal)) {
        return { valid: false, error: patternMessage };
      }
      if (verifyIban && !IBAN.isValid(polandCountryCode + currVal)) {
        return { valid: false, error: patternMessage };
      }

      return extraValidations.reduce(
        (acc, validation) => {
          if (acc.valid) {
            return validation(currVal);
          }
          return acc;
        },
        { valid: true, error: null },
      );
    },
    [required, pattern, error, touched],
  );

  useEffect(() => {
    setValidity({ valid: !error, error });
  }, [error, dirty]);

  useEffect(() => {
    setShowValidation((dirty || !validity.valid) && touched);
  }, [dirty, touched, validity.valid]);

  useEffect(() => {
    if (!validity.valid) {
      const validation = validate(val);
      setValidity(validation);
      setTouched(true);
      if (onDirty) {
        onDirty(val, validation);
      }
    }

    if (dirty) {
      const validation = validate(val);
      setValidity(validation);
      setTouched(true);
      if (onDirty) {
        onDirty(val, validation);
      }
    }
  }, [dirty, error, validity.valid]);

  useEffect(() => {
    if (value !== null && value !== val) {
      setVal(value);
      if (!validity.valid) {
        const validation = validate(value);
        setValidity(validation);
      }
    }
  }, [value]);

  // Same form component (eg. InvoiceForm) could use GenericForm with different set of fields
  // But some fields will be similar and their Input components will not re-render
  // This condition triggers reset of Input val and validity on form change
  useEffect(() => {
    if (formName) {
      setFormName(formName);
      if (formName !== currentFormName) {
        setVal(value);
        if (dirty) {
          setValidity(validate(value.trim()));
        } else {
          setValidity({ valid: true, error: null });
        }
      }
    }
  }, [formName]);

  const status = useMemo(() => {
    if (showValidation) {
      return validity.valid ? 'valid' : 'invalid';
    }
    return 'default';
  }, [showValidation, validity.valid]);

  const inputId = useMemo(() => (formName ? `${name}-${formName}` : name), [name, formName]);
  const inputName = useMemo(() => (formName ? `${name}-${formName}` : name), [name, formName]);

  const handleBlur = (event: any) => {
    const inputVal = event.target.value;
    setValidity(validate(inputVal.trim()));
    if (onBlur) {
      onBlur(inputVal);
    }
  };

  const handleChange = (event: any) => {
    let inputVal = event.target.value;
    if (maskFn) {
      inputVal = maskFn(inputVal);
    }
    const validation = validate(inputVal.trim());
    setVal(inputVal);
    setValidity(validation);
    if (onChange) {
      onChange(inputVal, validation);
    }
  };

  return (
    <S.Wrapper>
      <S.InputField
        placeholder={placeholder}
        label={label}
        labelProps={{ htmlFor: inputName }}
        status={status}
        error={i18n(validity.error, 'dlv_generics_error', validity.variables)}
        disabled={disabled}
        readOnly={readOnly}
        value={val}
        id={inputId}
        renderPrefix={prefixLabel && (() => prefixLabel)}
        onBlur={handleBlur}
        onChange={handleChange}
        name={inputName}
        type={type}
        required={required}
        size={flat && prefixLabel && val ? val.length : undefined}
        data-cy={`input-${name}`}
      />
    </S.Wrapper>
  );
}

export default memo(Input);
